import { createReducer, on } from '@ngrx/store';
import { initialState, LocationListState } from './location-list-state';
import { addressTranslationsAdapter, columnNotesAdapter, locationsAdapter } from './location-list-adapters';
import { ActiveDataConnectionsActions, AddressTranslationActions, BlacklistingActions, BulkValidationActions, CCCompletionActions, ColumnNotesActions, CountryListActions, EditGbpConnectionsActions, EditLocationActions, GbpLocationGroupsActions, LoadingSpinnerActions, LocationsLifecycleActions, LocationsRefreshActions, OngoingActions, RefreshGbpConnectionsActions, RowActions, SyncActions, ValidationHistoryActions } from './location-list-actions';

export const locationListReducer = createReducer(initialState,
    /*                                       Locations                                            */
    on(LocationsRefreshActions.getEntireLocationListSuccess, (state, { locations, lastSuccessfulPageSize }): LocationListState => {
      return {
        ...state,
        loading: false,
        locations: locationsAdapter.setAll(locations, state.locations),
        lastSuccessfulPageSize,
        addressTranslations: addressTranslationsAdapter.removeAll(state.addressTranslations),
      };
    }),
    // if component is being initiated and there are no locations, refresh is being called so set loading indicator
    on(LocationsLifecycleActions.locationsComponentInitiated, (state): LocationListState => ({
      ...state,
      loading: state.locations?.ids?.length ? state.loading : true,
    })),
    on(EditLocationActions.editLocationDataSuccess,
        CCCompletionActions.updateCcCompletionTimestampSuccess,
        LocationsRefreshActions.patchLocation,
        (state, { location }): LocationListState => ({
          ...state,
          locations: locationsAdapter.upsertOne(location, state.locations),
        })),
    on(LocationsRefreshActions.patchLocations,
        (state, { locations }): LocationListState => ({
          ...state,
          locations: locationsAdapter.upsertMany(locations, state.locations),
        })),
    on(LocationsRefreshActions.ignoreUpdateError, (state, { location}): LocationListState =>
        ({ ...state, locations: locationsAdapter.upsertOne(location, state.locations) })),
    on(LocationsRefreshActions.removedLocations, (state, { ids }): LocationListState => ({
      ...state,
      locations: locationsAdapter.removeMany(ids, state.locations),
      addressTranslations: addressTranslationsAdapter.removeMany(ids, state.addressTranslations),
    })),

    /*                                       AddressTranslations                                  */
    on(AddressTranslationActions.getAddressTranslationSuccess, (state, { addressTranslation }): LocationListState => ({
      ...state,
      addressTranslations: addressTranslationsAdapter.upsertOne(addressTranslation, state.addressTranslations),
    })),
    on(AddressTranslationActions.removeAddressTranslation, (state, { id }): LocationListState => ({
      ...state,
      addressTranslations: addressTranslationsAdapter.removeOne(id, state.addressTranslations),
    })),

    /*                                       GbpLocationGroup                                     */
    on(GbpLocationGroupsActions.updateGbpLocationGroupsSuccess, (state, { gbpLocationGroups }): LocationListState => ({
      ...state,
      gbpLocationGroups,
    })),
    on(ActiveDataConnectionsActions.loadActiveDataConnectionsSuccess, (state, { activeDataConnections }): LocationListState => ({
      ...state,
      activeDataConnections: activeDataConnections,
    })),

    /*                                           ColumnNotes                                      */
    on(ColumnNotesActions.deleteColumnNote, ColumnNotesActions.updateColumnNote, (state, { columnNote }): LocationListState => {
      const copy = { ...columnNote }; // copy coz it's an object from the store
      copy.loading = true; // activate loading spinner
      return {
        ...state,
        columnNotes: columnNotesAdapter.upsertOne(copy, state.columnNotes),
      };
    }),
    on(ColumnNotesActions.updateColumnNoteError, ColumnNotesActions.deleteColumnNoteError,
        (state, { columnNote }): LocationListState => {
          const copy = { ...columnNote }; // copy coz it's an object from the store
          copy.loading = false;  // stop spinner on error
          return {
            ...state,
            columnNotes: columnNotesAdapter.upsertOne(copy, state.columnNotes),
          };
        }),
    on(ColumnNotesActions.loadColumnNotesSuccess, (state, { columnNotes }): LocationListState => ({
      ...state,
      columnNotes: columnNotesAdapter.setAll(columnNotes, state.columnNotes),
    })),
    on(ColumnNotesActions.updateColumnNoteSuccess, (state, { columnNote }): LocationListState => {
      const columnNoteCopy = { ...columnNote };
      columnNoteCopy.loading = false;
      return {
        ...state,
        columnNotes: columnNotesAdapter.upsertOne(columnNoteCopy, state.columnNotes),
      };
    }),
    on(ColumnNotesActions.deleteColumnNoteSuccess, (state, { columnNoteName }): LocationListState => ({
      ...state,
      columnNotes: columnNotesAdapter.removeOne(columnNoteName, state.columnNotes),
    })),

    /*                            CountryList (country filter values)                             */
    on(CountryListActions.updateCountryListSuccess, (state, { countryList }): LocationListState => ({
      ...state,
      countryISO2List: countryList,
    })),

    on(RowActions.updateFocusedRow, (state, { id }): LocationListState => ({
      ...state,
      focusedRowId: id,
    })),
    on(RowActions.updateSelectedRowsIds, (state, { selectedRowsIds }): LocationListState => ({
      ...state,
      selectedRowsIds
    })),
    on(RowActions.updateHoveredRowId, (state, { hoveredRowId }): LocationListState => ({
      ...state,
      hoveredRowId
    })),

    /*                                         Blacklisting                                       */
    on(BlacklistingActions.blacklistFieldsSuccess, (state, { locations}): LocationListState => {
      return { ...state, locations: locationsAdapter.upsertMany(locations, state.locations) };
    }),


    /*                                         GBP Connections                                    */
    on(RefreshGbpConnectionsActions.refreshGbpConnectionsSuccess, (state, { locations}): LocationListState => {
      return { ...state, locations: locationsAdapter.upsertMany(locations, state.locations) };
    }),
    on(EditGbpConnectionsActions.editGbpConnectionsSuccess, (state, { res }): LocationListState => {
      return { ...state, locations: locationsAdapter.upsertOne(res.result, state.locations) };
    }),


    /*                                         LoadingStates                                      */
    // set loading bar overlay
    on(LocationsRefreshActions.getEntireLocationList, (state): LocationListState => ({ ...state, loading: true })),
    on(LoadingSpinnerActions.showLoadingSpinner, (state): LocationListState =>
        ({ ...state, loading: true })),
    on(LoadingSpinnerActions.stopLoadingSpinner, (state): LocationListState =>
        ({ ...state, loading: false })),
    on(OngoingActions.addOngoingAction, (state, { ongoingAction }): LocationListState => ({
      ...state,
      // deduplicate the currently ongoing actions using a `Set`
      ongoingActions: [...new Set([...state.ongoingActions].concat(ongoingAction))],
    })),
    on(OngoingActions.removeOngoingAction, (state, { ongoingAction }): LocationListState => ({
      ...state,
      ongoingActions: [...state.ongoingActions].filter(oa => oa !== ongoingAction),
    })),
    on(LocationsLifecycleActions.locationsComponentDestroyed, (state): LocationListState => ({
      ...state,
      loading: false,
    })),

    /*                                     Reset Data                                             */
    on(LocationsLifecycleActions.clearLocationsState, (state): LocationListState => ({
      ...initialState,
      loading: true,
    })),

    /*                               Sync details                                      */
    on(SyncActions.getDatasourceSyncDetailsSuccess,
        SyncActions.restartDatasourceSyncSuccess, (state, { datasourceSyncDetails }): LocationListState => ({
      ...state, datasourceSyncDetails
    })),
    on(SyncActions.getCCSyncDetailsSuccess,
        SyncActions.restartCCSyncSuccess, (state, { ccSyncDetails }): LocationListState => ({
          ...state, ccSyncDetails,
        })),

    /*                               Validation Data                                   */
    on(ValidationHistoryActions.getValidationHistory, (state): LocationListState => ({
      ...state, validationHistoryLoading: true,
    })),
    on(ValidationHistoryActions.getValidationHistorySuccess, (state, { validationHistory }): LocationListState => ({
      ...state, validationHistory: validationHistory, validationHistoryLoading: false,
    })),
    on(ValidationHistoryActions.getValidationHistoryFail, (state): LocationListState => ({
      ...state, validationHistoryLoading: false,
    })),
    on(ValidationHistoryActions.resetValidationHistory, (state): LocationListState => ({
      ...state, validationHistory: [], validationHistoryLoading: false,
    })),

    on(BulkValidationActions.getBulkValidationDetailsSuccess, (state, { details }): LocationListState => ({
      ...state, bulkValidationDetails: details,
    })),
);

